import {forwardRef, ForwardRefRenderFunction, useId} from 'react';
import './flat-date-input.scss';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {MobileDatePicker} from '@mui/x-date-pickers';
import {translations} from '../../i18n/translations';
import {FormControl, Slide, TextField, IconButton, Box, Typography} from '@mui/material';
import {MobileDatePickerProps} from '@mui/lab';
import {ArrowDropDown} from '@mui/icons-material';
import {Pallete} from '../../theme/bluebird';

interface FlatDateInputProps extends MobileDatePickerProps<Date | undefined> {
  label: string;
  disableFuture: boolean;
  value?: Date;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  errors?: string;
  required?: boolean;
}

/**
 * Date input.
 */

const FlatDateInputComponent: ForwardRefRenderFunction<
  HTMLInputElement,
  Omit<FlatDateInputProps, 'ref'>
> = (
  {label, className, disabled, errors, placeholder, value, name, required, disableFuture, ...rest},
  ref
) => {
  const {t} = useTranslation();
  const hasError = !!errors;
  const id = useId();
  const uniqueId = rest.id ?? `date_input_${id}`;

  return (
    <FormControl className={`flat-date-input ${disabled ? 'disabled' : ''}`}>
      {label && (
        <Box className="label-container">
          <Typography component="label" htmlFor={uniqueId} noWrap>
            {label}
          </Typography>
          {required && !disabled && <Typography component="span">*</Typography>}
        </Box>
      )}
      <MobileDatePicker
        value={moment(value || undefined)}
        {...rest}
        localeText={{
          cancelButtonLabel: t(translations.date_time_picker.cancel_button),
          clearButtonLabel: t(translations.date_time_picker.clear_button),
        }}
        slots={{
          toolbar: () => <></>,
          textField: (params) => (
            <TextField
              inputRef={ref}
              id={id}
              {...params}
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                value: value ? moment(new Date(value)).format('LL') : '',
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    sx={{
                      width: '28px',
                      height: '28px',
                    }}
                  >
                    <ArrowDropDown sx={{fontSize: '20px'}} />
                  </IconButton>
                ),
              }}
              fullWidth
              variant="filled"
              sx={rest.sx}
              error={hasError}
            />
          ),
        }}
        slotProps={{
          actionBar: {actions: ['clear', 'cancel']},
        }}
        closeOnSelect
      />
      <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
        <span className="input-error" style={{color: Pallete.red.base}}>
          {errors}
        </span>
      </Slide>
    </FormControl>
  );
};

// A function component only gets a (frozen) context as its second argument, so it has to be wrapped to receive the ref
export const FlatDateInput = forwardRef(FlatDateInputComponent);
